// CSV export — BJJ detailed-journal sessions.
//
// One row per session. Multi-value fields (techniques, partners, focus
// modes, etc.) are semicolon-delimited within their column. This is the
// single-denormalized-file format the user picked; serious analysts can
// pivot from this in Excel/Sheets.
//
// User-defined value axiom: column headers describe what the user
// recorded, not what an arbitrary taxonomy says they "should have"
// recorded. Missing fields ship as empty strings.
//
// Anti-shame rule: we DON'T compute derived "performance" metrics here
// (subs-per-roll ratios, etc.). The user can build those themselves if
// they want them. We export what was logged.

import { formatPosition } from '../data/combatives';

// RFC 4180 CSV escape: wrap in double quotes when the value contains
// comma, double-quote, newline, or starts with whitespace. Double up
// any internal quotes.
const esc = (raw) => {
  const s = raw == null ? '' : String(raw);
  if (/[",\n\r]/.test(s) || /^\s/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

// Flatten a techniques array into a readable string column. Each
// technique becomes a `;` -delimited entry: "Position / Subposition · Name
// [Submission] [Mastery] [WeaponMode: Weapon]".
const formatTechniques = (techs = []) => {
  return techs.map((t) => {
    const parts = [];
    const pos = formatPosition(t.position);
    if (pos) parts.push(pos);
    parts.push(t.name || '(unnamed)');
    const extras = [];
    if (t.submission) extras.push(`sub: ${t.submission}`);
    if (t.mastery) extras.push(t.mastery);
    if (t.reps) extras.push(`${t.reps} reps`);
    if (t.weapon) extras.push(`${t.weaponMode || 'Weapon'}: ${t.weapon}`);
    if (extras.length) parts.push(`[${extras.join(', ')}]`);
    return parts.join(' · ');
  }).join('; ');
};

// Flatten steps array — order-numbered, with "*" suffix if needsDrilling.
const formatSteps = (techs = []) => {
  return techs.map((t) => {
    const steps = (t.steps || []).map((s, i) => `${i + 1}. ${s.text || ''}${s.needsDrilling ? ' *' : ''}`).join(' | ');
    return steps ? `${t.name || '(unnamed)'}: ${steps}` : '';
  }).filter(Boolean).join('; ');
};

// Build a single CSV row for a session.
const sessionToRow = (s) => {
  const sp = s.sparring || {};
  const r = s.reflection || {};
  const inj = s.injury || {};
  const date = s.date ? new Date(s.date) : null;
  return [
    date ? date.toISOString().slice(0, 10) : '',
    date ? date.toLocaleString(undefined, { weekday: 'long' }) : '',
    s.duration || '',
    (s.classFormat || []).join('; '),
    (s.focusModes || []).join('; '),
    (s.coaches || []).join('; '),
    s.beltSnapshot?.rankId || '',
    s.beltSnapshot?.stripes ?? '',
    s.intent || '',
    r.intentHit === true ? 'Yes' : r.intentHit === false ? 'No' : r.intentHit === 'partial' ? 'Partial' : '',
    r.win || '',
    r.gap || '',
    r.nextFocus || '',
    (s.techniques || []).length,
    formatTechniques(s.techniques),
    formatSteps(s.techniques),
    sp.rolls ?? '',
    (sp.partners || []).join('; '),
    sp.feltScale ?? '',
    sp.subsLanded ?? '',
    sp.subsCaught ?? '',
    sp.sweepsLanded ?? '',
    sp.sweepsAllowed ?? '',
    sp.passesLanded ?? '',
    sp.passesAllowed ?? '',
    sp.takedownsLanded ?? '',
    sp.takedownsConceded ?? '',
    sp.note || '',
    inj.bodyPart || '',
    inj.note || '',
    s.notes || '',
    (s.attachments || []).length,
  ].map(esc).join(',');
};

const HEADERS = [
  'Date', 'Day', 'Duration (min)',
  'Class format', 'Focus modes', 'Coaches',
  'Belt at time', 'Stripes at time',
  'Intent', 'Intent accomplished', 'Win', 'Gap', 'Next focus',
  '# Techniques', 'Techniques (summary)', 'Technique steps',
  'Rolls', 'Partners', 'Felt (1-5)',
  'Subs landed', 'Subs caught in', 'Sweeps landed', 'Sweeps allowed',
  'Passes landed', 'Passes allowed', 'Takedowns landed', 'Takedowns conceded',
  'Sparring note',
  'Injury body part', 'Injury note',
  'General notes', '# Attachments',
];

/**
 * Build a CSV string from a list of BJJ sessions. Sessions are sorted
 * newest-first in the output regardless of input order.
 */
export const buildBJJCSV = (sessions = []) => {
  const sorted = [...sessions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const rows = [HEADERS.join(',')];
  sorted.forEach((s) => rows.push(sessionToRow(s)));
  return rows.join('\n');
};

/**
 * Trigger a browser download of the BJJ sessions as a CSV file.
 * Filename includes today's date for easy re-download tracking.
 */
export const downloadBJJCSV = (sessions = []) => {
  const csv = buildBJJCSV(sessions);
  // BOM so Excel opens UTF-8 cleanly.
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-bjj-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ============= Beta feedback exporter =============
// One row per submitted feedback note. Newest-first. Used by the admin
// FeedbackInbox so the founder can pull the full beta-feedback log into
// Sheets/Excel for triage and roadmap sourcing.
const FEEDBACK_HEADERS = [
  'Date', 'Category', 'Status', 'From', 'Page', 'Message', 'User agent',
];

const feedbackToRow = (f) => [
  f.created_at ? new Date(f.created_at).toISOString().slice(0, 10) : '',
  f.category || '',
  f.status || '',
  f.user_email || '',
  f.page || '',
  f.message || '',
  f.user_agent || '',
].map(esc).join(',');

export const buildFeedbackCSV = (items = []) => {
  const sorted = [...items].sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
  );
  const rows = [FEEDBACK_HEADERS.join(',')];
  sorted.forEach((f) => rows.push(feedbackToRow(f)));
  return rows.join('\n');
};

export const downloadFeedbackCSV = (items = []) => {
  const csv = buildFeedbackCSV(items);
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-beta-feedback-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ============= Muay Thai exporter ============= //

const formatMTTechniques = (techs = []) => {
  return techs.map((t) => {
    const parts = [];
    if (t.range) parts.push(t.range);
    if (t.type) parts.push(t.type);
    parts.push(t.name || '(unnamed)');
    const extras = [];
    if (t.mastery) extras.push(t.mastery);
    if (t.reps) extras.push(`${t.reps} reps`);
    if (extras.length) parts.push(`[${extras.join(', ')}]`);
    return parts.join(' · ');
  }).join('; ');
};

const mtSessionToRow = (s) => {
  const sp = s.sparring || {};
  const r = s.reflection || {};
  const inj = s.injury || {};
  const date = s.date ? new Date(s.date) : null;
  return [
    date ? date.toISOString().slice(0, 10) : '',
    date ? date.toLocaleString(undefined, { weekday: 'long' }) : '',
    s.duration || '',
    (s.classFormat || []).join('; '),
    (s.focusModes || []).join('; '),
    (s.coaches || []).join('; '),
    s.beltSnapshot?.rankId || '',
    s.intent || '',
    r.intentHit === true ? 'Yes' : r.intentHit === false ? 'No' : r.intentHit === 'partial' ? 'Partial' : '',
    r.win || '',
    r.gap || '',
    r.nextFocus || '',
    (s.techniques || []).length,
    formatMTTechniques(s.techniques),
    sp.rounds ?? '',
    (sp.partners || []).join('; '),
    sp.feltScale ?? '',
    sp.knockdownsLanded ?? '',
    sp.knockdownsAbsorbed ?? '',
    sp.note || '',
    inj.bodyPart || '',
    inj.note || '',
    s.notes || '',
    (s.attachments || []).length,
  ].map(esc).join(',');
};

const MT_HEADERS = [
  'Date', 'Day', 'Duration (min)',
  'Class format', 'Focus modes', 'Coaches', 'Rank at time',
  'Intent', 'Intent accomplished', 'Win', 'Gap', 'Next focus',
  '# Techniques', 'Techniques (summary)',
  'Rounds', 'Partners', 'Felt (1-5)',
  'Knockdowns landed', 'Knockdowns absorbed',
  'Sparring note',
  'Injury body part', 'Injury note',
  'General notes', '# Attachments',
];

export const buildMuayThaiCSV = (sessions = []) => {
  const sorted = [...sessions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const rows = [MT_HEADERS.join(',')];
  sorted.forEach((s) => rows.push(mtSessionToRow(s)));
  return rows.join('\n');
};

export const downloadMuayThaiCSV = (sessions = []) => {
  const csv = buildMuayThaiCSV(sessions);
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-muaythai-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ============= Generic exporter (for arts without their own schema) =============
// Generic CSV builder for arts that don't yet have a rich-journal schema.
// Handles the simple session shape: { date, focus, duration, notes,
// attachments, artName }. New arts will swap in their own builder once
// they get their own detailed-journal fork.
const genericSessionToRow = (s) => {
  const date = s.date ? new Date(s.date) : null;
  return [
    date ? date.toISOString().slice(0, 10) : '',
    date ? date.toLocaleString(undefined, { weekday: 'long' }) : '',
    s.artName || s.artId || '',
    s.focus || '',
    s.duration || '',
    s.notes || '',
    (s.attachments || []).length,
  ].map(esc).join(',');
};

const GENERIC_HEADERS = ['Date', 'Day', 'Art', 'Focus', 'Duration (min)', 'Notes', '# Attachments'];

export const buildGenericCSV = (sessions = []) => {
  const sorted = [...sessions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const rows = [GENERIC_HEADERS.join(',')];
  sorted.forEach((s) => rows.push(genericSessionToRow(s)));
  return rows.join('\n');
};

/**
 * Trigger a browser download of generic-shape sessions as a CSV. Used by
 * art surfaces that don't yet have a rich-journal exporter. Filename
 * includes the art id + today's date.
 */
export const downloadGenericCSV = (sessions = [], artId = 'training') => {
  const csv = buildGenericCSV(sessions);
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-${artId}-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};


// =====================================================================
// Judo CSV exporter —
//
// One row per Judo session. Multi-value fields (techniques, partners,
// focus modes) collapse with ';' inside their column. Mirrors the BJJ
// shape philosophically (denormalized single-file) but the columns are
// Judo-canonical: classification = group / subgroup / technique name,
// randori stats = throws-completed-vs-countered + GW submissions, etc.
// =====================================================================

const formatJudoTechniques = (techniques) => {
  if (!techniques || techniques.length === 0) return '';
  return techniques.map((t) => {
    const c = t.classification || {};
    const cls = [c.group, c.subgroup, c.name].filter(Boolean).join(' / ');
    const meta = [];
    if (t.outcome) meta.push(t.outcome);
    if (t.reps) meta.push(`x${t.reps}`);
    if (t.partnerLevel) meta.push(`(${t.partnerLevel})`);
    if (t.mastery) meta.push(`[${t.mastery}]`);
    return `${cls} ${meta.join(' ')}`.trim();
  }).join('; ');
};

export const downloadJudoCSV = (sessions) => {
  const headers = [
    'date', 'duration_min', 'class_format', 'focus_modes', 'belt_snapshot',
    'coaches', 'intent', 'technique_count', 'techniques',
    'randori_rounds', 'throws_completed', 'throws_countered',
    'gw_subs_landed', 'gw_subs_given', 'randori_felt_scale', 'randori_partners',
    'randori_notes',
    'reflection_intent_hit', 'reflection_win', 'reflection_gap', 'reflection_next_focus',
    'session_notes',
  ];

  const rows = (sessions || []).map((s) => {
    const r = s.randori || {};
    const ref = s.reflection || {};
    return [
      s.date ? new Date(s.date).toISOString().slice(0, 10) : '',
      s.duration || '',
      s.classFormat || '',
      (s.focusModes || []).join('; '),
      s.beltSnapshot || '',
      (s.coaches || []).join('; '),
      s.intent || '',
      (s.techniques || []).length,
      formatJudoTechniques(s.techniques),
      r.rounds || 0,
      r.throwsCompleted || 0,
      r.throwsCountered || 0,
      r.groundworkSubmissions || 0,
      r.groundworkSubmitted || 0,
      r.feltScale || '',
      (r.partners || []).join('; '),
      r.notes || '',
      ref.intentHit || '',
      ref.win || '',
      ref.gap || '',
      ref.nextFocus || '',
      s.notes || '',
    ].map(esc).join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-judo-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};


// =====================================================================
// Wrestling CSV exporter —
//
// One row per Wrestling session. Wrestling-specific columns: style
// (folkstyle/freestyle/greco), weight & hydration block, coach feedback
// (semicolon-joined), match results (semicolon-joined). Style-conditional
// fields (riding time, push-outs) are always-present columns; empty for
// sessions where the style doesn't use them.
// =====================================================================

const formatWrestlingTechniques = (techniques) => {
  if (!techniques || techniques.length === 0) return '';
  return techniques.map((t) => {
    const parts = [t.phase, t.subgroup, t.name].filter(Boolean).join(' / ');
    const meta = [];
    if (t.reps) meta.push(`x${t.reps}`);
    if (t.mastery) meta.push(`[${t.mastery}]`);
    return `${parts}${meta.length ? ' ' + meta.join(' ') : ''}`.trim();
  }).join('; ');
};

const formatWrestlingCoach = (coachFeedback) => {
  if (!coachFeedback || coachFeedback.length === 0) return '';
  return coachFeedback.map((c) => `${c.topic ? c.topic + ': ' : ''}${c.note || ''}`.trim()).join('; ');
};

const formatWrestlingMatches = (matches) => {
  if (!matches || matches.length === 0) return '';
  return matches.map((m) => {
    const parts = [(m.result || '').toUpperCase()];
    if (m.opponent) parts.push(`vs ${m.opponent}`);
    if (m.score) parts.push(m.score);
    return parts.join(' ');
  }).join('; ');
};

export const downloadWrestlingCSV = (sessions) => {
  const headers = [
    'date', 'duration_min', 'style', 'class_format', 'focus_modes', 'intent',
    'technique_count', 'techniques',
    'live_goes', 'partners', 'intensity_1_5',
    'tds_scored', 'tds_conceded', 'escapes_earned', 'escapes_conceded',
    'reversals_scored', 'reversals_conceded',
    'turns_scored', 'turns_conceded', 'pins_scored', 'pins_conceded',
    'riding_time_seconds', 'pushouts_scored', 'pushouts_conceded',
    'coach_feedback',
    'match_count', 'matches',
    'weight_lb', 'target_class_lb', 'days_to_weighin', 'cut_method', 'hydration',
    'weight_notes',
    'injury_concerns',
    'reflection', 'session_notes',
  ];

  const rows = (sessions || []).map((s) => {
    const sp = s.sparring || {};
    const w = s.weight || {};
    const injuries = (s.injuryConcerns || []).map((i) =>
      `${i.area || 'area?'}${i.severity ? ` (${i.severity}/5)` : ''}${i.note ? ': ' + i.note : ''}`,
    ).join('; ');
    return [
      s.date ? new Date(s.date).toISOString().slice(0, 10) : '',
      s.duration || '',
      s.style || '',
      s.classFormat || '',
      (s.focusModes || []).join('; '),
      s.intent || '',
      (s.techniques || []).length,
      formatWrestlingTechniques(s.techniques),
      sp.liveGoCount || '',
      sp.partnersWorkedWith || '',
      sp.intensityRating || '',
      sp.takedownsScored || '',
      sp.takedownsConceded || '',
      sp.escapesEarned || '',
      sp.escapesConceded || '',
      sp.reversalsScored || '',
      sp.reversalsConceded || '',
      sp.turnsScored || '',
      sp.turnsConceded || '',
      sp.pinsScored || '',
      sp.pinsConceded || '',
      sp.ridingTimeSeconds || '',
      sp.pushoutsScored || '',
      sp.pushoutsConceded || '',
      formatWrestlingCoach(s.coachFeedback),
      (s.matches || []).length,
      formatWrestlingMatches(s.matches),
      w.scaleWeightLb || '',
      w.targetClassLb || '',
      w.daysToWeighIn || '',
      w.cutMethod || '',
      w.hydrationStatus || '',
      w.notes || '',
      injuries,
      s.reflection || '',
      s.notes || '',
    ].map(esc).join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-wrestling-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};


// =====================================================================
// Boxing CSV exporter —
//
// One row per Boxing session. Boxing-specific columns: stance, glove
// weight, partner experience, structured combinations (sequence + label
// + reps), rounds-based sparring totals (clean lands, knockdowns), match
// results with W/L/Draw + free-text finish field, weight & hydration
// block (collapsed-by-default UX, present whenever data is logged).
// =====================================================================

const formatBoxingTechniques = (techniques) => {
  if (!techniques || techniques.length === 0) return '';
  return techniques.map((t) => {
    const parts = [t.group, t.subgroup, t.name].filter(Boolean).join(' / ');
    const meta = [];
    if (t.reps) meta.push(`x${t.reps}`);
    if (t.mastery) meta.push(`[${t.mastery}]`);
    return `${parts}${meta.length ? ' ' + meta.join(' ') : ''}`.trim();
  }).join('; ');
};

const formatBoxingCombos = (combos) => {
  if (!combos || combos.length === 0) return '';
  return combos.map((c) => {
    const parts = [c.sequence, c.label].filter(Boolean).join(' — ');
    return `${parts}${c.reps ? ` x${c.reps}` : ''}`.trim();
  }).join('; ');
};

const formatBoxingCoach = (coachFeedback) => {
  if (!coachFeedback || coachFeedback.length === 0) return '';
  return coachFeedback.map((c) => `${c.topic ? c.topic + ': ' : ''}${c.note || ''}`.trim()).join('; ');
};

const formatBoxingMatches = (matches) => {
  if (!matches || matches.length === 0) return '';
  return matches.map((m) => {
    const parts = [(m.result || '').toUpperCase()];
    if (m.opponent) parts.push(`vs ${m.opponent}`);
    if (m.finish) parts.push(m.finish);
    return parts.join(' ');
  }).join('; ');
};

const formatBoxingRoundNotes = (rounds) => {
  if (!rounds || rounds.length === 0) return '';
  return rounds.map((r) => `R${r.roundNum || '?'}: ${r.note || ''}`.trim()).join('; ');
};

export const downloadBoxingCSV = (sessions) => {
  const headers = [
    'date', 'duration_min', 'stance', 'class_format', 'focus_modes', 'intent',
    'technique_count', 'techniques',
    'combo_count', 'combinations',
    'sparring_rounds', 'round_length_sec', 'glove_weight_oz', 'partner_level',
    'intensity_1_5',
    'clean_lands_scored', 'clean_lands_conceded',
    'knockdowns_scored', 'knockdowns_conceded',
    'per_round_notes', 'sparring_note',
    'coach_feedback',
    'match_count', 'matches',
    'weight_lb', 'target_class_lb', 'days_to_weighin', 'cut_method', 'hydration',
    'weight_notes',
    'injury_concerns',
    'reflection', 'session_notes',
  ];

  const rows = (sessions || []).map((s) => {
    const sp = s.sparring || {};
    const w = s.weight || {};
    const injuries = (s.injuryConcerns || []).map((i) =>
      `${i.area || 'area?'}${i.severity ? ` (${i.severity}/5)` : ''}${i.note ? ': ' + i.note : ''}`,
    ).join('; ');
    return [
      s.date ? new Date(s.date).toISOString().slice(0, 10) : '',
      s.duration || '',
      s.stance || '',
      s.classFormat || '',
      (s.focusModes || []).join('; '),
      s.intent || '',
      (s.techniques || []).length,
      formatBoxingTechniques(s.techniques),
      (s.combinations || []).length,
      formatBoxingCombos(s.combinations),
      sp.roundsCompleted || '',
      sp.roundLengthSec || '',
      sp.gloveWeightOz || '',
      sp.partnerExperienceLevel || '',
      sp.intensityRating || '',
      sp.cleanLandsScored || '',
      sp.cleanLandsConceded || '',
      sp.knockdownsScored || '',
      sp.knockdownsConceded || '',
      formatBoxingRoundNotes(sp.rounds),
      sp.note || '',
      formatBoxingCoach(s.coachFeedback),
      (s.matches || []).length,
      formatBoxingMatches(s.matches),
      w.scaleWeightLb || '',
      w.targetClassLb || '',
      w.daysToWeighIn || '',
      w.cutMethod || '',
      w.hydrationStatus || '',
      w.notes || '',
      injuries,
      s.reflection || '',
      s.notes || '',
    ].map(esc).join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `enferno-boxing-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
